import axios from 'axios';

const API_URL = 'https://api-id-dev.minimarket116.kz/api/v1'; // Замените на ваш домен
// Создание экземпляра axios с базовым URL
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Функции для работы с токенами
const tokenManager = {
    // Получение access token из localStorage
    getAccessToken: () => localStorage.getItem('access_token'),

    // Получение refresh token из localStorage
    getRefreshToken: () => localStorage.getItem('refresh_token'), // временное решение для совместимости

    // Получение времени истечения токена
    getTokenExpiration: () => {
        const expiresAt = localStorage.getItem('access_token_expires_at');
        return expiresAt ? parseInt(expiresAt, 10) : null;
    },

    // Установка токенов
    setTokens: (accessToken, refreshToken, user, expiresIn) => {
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken); // временное решение для совместимости

        // Сохраняем время истечения токена, если передан expiresIn
        if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000; // Переводим секунды в миллисекунды и добавляем к текущему времени
            localStorage.setItem('access_token_expires_at', expiresAt.toString());
        }

        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        }
    },

    // Очистка токенов
    clearTokens: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token_expires_at');
        localStorage.removeItem('user');
        localStorage.removeItem('temp_token');
        localStorage.removeItem('temp_user');
        // Cookie будут удалены на сервере при запросе logout
    },

    // Проверка, истечет ли токен в течение указанного времени (в миллисекундах)
    willTokenExpireSoon: (timeThreshold = 5 * 60 * 1000) => { // По умолчанию 5 минут
        const expiresAt = tokenManager.getTokenExpiration();
        if (!expiresAt) return true; // Если нет данных о времени истечения, считаем, что токен скоро истечет

        const timeUntilExpiration = expiresAt - Date.now();
        return timeUntilExpiration <= timeThreshold;
    }
};

// Интерцептор для добавления токена авторизации
api.interceptors.request.use(
    (config) => {
        const accessToken = tokenManager.getAccessToken();
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Флаг для отслеживания выполнения рефреша токена
let isRefreshing = false;
// Очередь запросов, ожидающих обновления токена
let failedQueue = [];

// Функция для обработки очереди запросов
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Обновление токена
const refreshAuthToken = async () => {
    try {
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
            throw new Error('Refresh token not available');
        }

        const response = await axios.post(`${API_URL}/auth/refresh`, {refresh_token: refreshToken});

        if (response.data.status === 'success') {
            const {access_token, refresh_token, user, expires_in} = response.data.data;
            tokenManager.setTokens(access_token, refresh_token, user, expires_in);
            return access_token;
        } else {
            throw new Error(response.data.message || 'Failed to refresh token');
        }
    } catch (error) {
        tokenManager.clearTokens();
        // Сохраняем OAuth параметры перед редиректом
        const oauthParams = localStorage.getItem('oauth_params');
        if (oauthParams) {
            window.location.href = '/login';
        } else {
            window.location.href = '/login';
        }
        throw error;
    }
};

// Обновленный перехватчик ответов
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если ошибка не 401 или это уже повторный запрос после рефреша, просто отклоняем
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Проверяем, не является ли это запросом на рефреш токена
        if (originalRequest.url === '/auth/refresh') {
            tokenManager.clearTokens();
            // Проверяем наличие OAuth параметров перед редиректом
            const oauthParams = localStorage.getItem('oauth_params');
            window.location.href = oauthParams ? '/login' : '/login';
            return Promise.reject(error);
        }

        // Если уже идет процесс обновления токена, добавляем запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject});
            })
                .then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return api(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        // Начинаем процесс обновления токена
        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshAuthToken();
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            isRefreshing = false;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;
            return Promise.reject(refreshError);
        }
    }
);

// Аутентификация
export const authService = {
    login: async (username, password) => {
        try {
            const response = await api.post('/auth', {username, password});

            // Обработка двухфакторной аутентификации
            if (response.data.status === 'pending_2fa') {
                const {temp_token, user} = response.data.data;
                localStorage.setItem('temp_token', temp_token);
                localStorage.setItem('temp_user', JSON.stringify(user));
                window.location.href = '/two-factor-auth';
                return {success: false, pending2FA: true};
            }

            // Обычная успешная аутентификация
            if (response.data.status === 'success') {
                const {access_token, refresh_token, user, expires_in} = response.data.data;
                tokenManager.setTokens(access_token, refresh_token, user, expires_in);
                return {success: true, user};
            }

            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при попытке входа',
            };
        }
    },

    verify2FA: async (code) => {
        try {
            const temp_token = localStorage.getItem('temp_token');
            if (!temp_token) {
                return {success: false, message: 'Отсутствует временный токен'};
            }

            const response = await api.post('/auth/2fa', {
                temp_token,
                code
            });

            if (response.data.status === 'success') {
                // Очищаем временные данные
                localStorage.removeItem('temp_token');
                localStorage.removeItem('temp_user');

                // Сохраняем данные аутентификации
                const {access_token, refresh_token, user, expires_in} = response.data.data;
                tokenManager.setTokens(access_token, refresh_token, user, expires_in);

                return {success: true, user};
            }

            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Неверный код аутентификации'
            };
        }
    },

    logout: async (navigate) => {
        try {

            const refreshToken = tokenManager.getRefreshToken();

            if (refreshToken) {
                // Вызываем API для выхода на сервере и очистки серверных токенов
                await api.post('/auth/logout', {refresh_token: refreshToken});
            }
        } catch (error) {
            console.error('Ошибка при выходе из системы:', error);
        } finally {
            // Всегда очищаем локальные токены
            tokenManager.clearTokens();
            // Редирект на страницу логина
            if (navigate) {
                navigate('/login');
            } else {
                window.location.href = '/login';
            }
        }
    },

    isPending2FA: () => {
        return !!localStorage.getItem('temp_token');
    },

    cancel2FA: () => {
        localStorage.removeItem('temp_token');
        localStorage.removeItem('temp_user');
    },

    hasTemporaryPassword: () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return user.is_temporary_password === 1;
        }
        return false;
    },

    changeTemporaryPassword: async (temporaryPassword, newPassword) => {
        try {
            const response = await api.post('/password/temporary', {
                temporary_password: temporaryPassword,
                new_password: newPassword
            });

            if (response.data.status === 'success') {
                // Обновляем статус временного пароля в localStorage
                const currentUser = JSON.parse(localStorage.getItem('user'));
                if (currentUser) {
                    currentUser.is_temporary_password = 0;
                    localStorage.setItem('user', JSON.stringify(currentUser));
                }

                return {
                    success: true,
                    message: response.data.message
                };
            }
            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при изменении пароля'
            };
        }
    },

    getCurrentUser: () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            return JSON.parse(userStr);
        }
        return null;
    },

    isAuthenticated: () => {
        return !!tokenManager.getAccessToken() && !!tokenManager.getRefreshToken();
    },
};

// Сервис работы с пользователями
export const userService = {
    // Получить профиль пользователя
    getUserProfile: async (userId) => {
        try {
            const response = await api.get(`/users/${userId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении профиля');
        }
    },

    // Обновить профиль пользователя
    updateUserProfile: async (userId, userData) => {
        try {
            const response = await api.put(`/users/${userId}`, userData);
            if (response.data.status === 'success') {
                // Обновляем данные пользователя в localStorage
                const currentUser = JSON.parse(localStorage.getItem('user'));
                const updatedUser = {...currentUser, ...userData};
                localStorage.setItem('user', JSON.stringify(updatedUser));
                return {success: true, message: response.data.message};
            }
            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при обновлении профиля'
            };
        }
    },

    // Получить список всех пользователей (только для админов)
    getAllUsersList: async () => {
        try {
            const response = await api.get('/users');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка пользователей');
        }
    },

    // Создать нового пользователя
    createUser: async (userData) => {
        try {
            const response = await api.post('/users', userData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Сбросить пароль пользователя
    resetUserPassword: async (userId) => {
        try {
            const response = await api.post(`/users/${userId}/reset-password`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Получение списка внешних ролей
    getExternalRoles: async () => {
        try {
            const response = await api.get('/external-roles');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка внешних ролей');
        }
    },

    // Получение списка складов wms
    getWarehousesList: async () => {
        try {
            const response = await api.get('/warehouses');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка складов');
        }
    },
};

// Сервис безопасности
export const securityService = {
    // Изменение пароля
    changePassword: async (oldPassword, newPassword) => {
        try {
            const response = await api.post('/password', {
                old_password: oldPassword,
                new_password: newPassword
            });
            return {
                success: response.data.status === 'success',
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при изменении пароля'
            };
        }
    },

    // Запрос на изменение email
    requestEmailChange: async (oldEmail, newEmail) => {
        try {
            const response = await api.post('/email/change', {
                old_email: oldEmail,
                new_email: newEmail
            });

            if (response.data.status === 'success') {
                return {
                    success: true,
                    message: response.data.message,
                    expiresAt: response.data.data.expires_at
                };
            }
            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при изменении email'
            };
        }
    },

    // Подтверждение изменения email кодом
    verifyEmailChange: async (code) => {
        try {
            const response = await api.post('/email/verify', {code});

            if (response.data.status === 'success') {
                // Обновляем email в localStorage
                const currentUser = JSON.parse(localStorage.getItem('user'));
                if (currentUser) {
                    currentUser.email = response.data.data.email;
                    localStorage.setItem('user', JSON.stringify(currentUser));
                }

                return {
                    success: true,
                    message: response.data.message,
                    email: response.data.data.email
                };
            }
            return {success: false, message: response.data.message};
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Ошибка при проверке кода'
            };
        }
    },

    // Получение информации о 2FA пользователя (только для админов)
    get2FAInfo: async (userId) => {
        try {
            const response = await api.get(`/2fa/${userId}/info`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Установка 2FA
    setup2FA: async () => {
        try {
            const response = await api.post('/2fa/setup');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Проверка и активация 2FA
    verify2FA: async (code, secret) => {
        try {
            const response = await api.post('/2fa/verify', {code, secret});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Отключение 2FA
    disable2FA: async (code) => {
        try {
            const response = await api.post('/2fa/disable', {code});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Сброс 2FA пользователя (только для админов)
    reset2FA: async (userId) => {
        try {
            const response = await api.post('/2fa/admin-reset', {user_id: userId});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Получение логов попыток авторизации
    getAuthAttemptLogs: async () => {
        try {
            const response = await api.get('/auth/logs/auth-attempts');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении логов авторизации');
        }
    },

    // Получение логов блокировок
    getAuthBlockLogs: async () => {
        try {
            const response = await api.get('/auth/logs/auth-blocks');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении логов блокировок');
        }
    },

};

// Сервис OAuth авторизации
export const oauthService = {
    // Получение информации о клиенте OAuth
    getClientInfo: async (clientId, redirectUri) => {
        try {
            const response = await api.get(`/oauth/client-info`, {
                params: {
                    client_id: clientId,
                    redirect_uri: redirectUri
                }
            });
            return response.data;
        } catch (error) {
            return {
                status: 'error',
                message: error.response?.data?.message || 'Ошибка при получении информации о клиенте'
            };
        }
    },

    // Авторизация OAuth
    authorize: async (params) => {
        try {
            const response = await api.get('/oauth/authorize', {
                params: {
                    client_id: params.client_id,
                    redirect_uri: params.redirect_uri,
                    response_type: params.response_type,
                    scope: params.scope,
                    state: params.state,
                    code_challenge: params.code_challenge,
                    code_challenge_method: params.code_challenge_method,
                    auth_request_id: params.auth_request_id
                }
            });
            return response.data;
        } catch (error) {
            return {
                status: 'error',
                message: error.response?.data?.message || 'Ошибка при авторизации'
            };
        }
    }
};

export default api;